// Tenancy contracts — organisation, branches, feature flags, the PIN-protected action set and the setting store
// (modules-and-api.md §5.3; TEN-13, TEN-14, TEN-16, TEN-20..23, TEN-48; BR-TENANCY-003..014).
import { z } from "zod";
import { ALWAYS_REQUIRED_ACTIONS, CONTROL_LOG_ACTIONS } from "./auth";
import { BpSchema, BranchCodeSchema, InstantSchema, NonNegativeCentsSchema, UlidSchema } from "./primitives";
import { SUPPLIER_STATUSES } from "./purchasing";

export const COMMERCIAL_MODELS = ["Independent", "Group", "Franchisor", "Franchisee"] as const;
export type CommercialModelCode = (typeof COMMERCIAL_MODELS)[number];
export const ORGANISATION_STATUSES = ["Active", "Suspended", "Offboarding", "Closed"] as const;
export const PATIENT_SHARING_MODES = ["Tenant", "Branch"] as const;
export const ACCOUNTING_PROVIDERS = ["None", "Xero", "ZohoBooks", "Sage"] as const;
export const CRM_PROVIDERS = ["None", "Zoho", "Native"] as const;
export const BRANCH_STATUSES = ["Active", "Inactive"] as const;
export const FEATURE_FLAG_KEYS = ["claims", "hoyalog", "franchise_fees", "crm_connector", "self_service_portal", "webauthn_stepup", "offline_writes", "api_clients"] as const;
export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];

/**
 * BR-TENANCY-014: retention is a whole number of years or `indefinite`. The organisation row stores an integer, so
 * `indefinite` is stored as this sentinel and rendered back as the word. Any stored value at or above it reads as indefinite.
 */
export const RETENTION_INDEFINITE_YEARS = 1000;
export const RetentionYearsSchema = z.union([z.number().int().min(0).max(RETENTION_INDEFINITE_YEARS - 1), z.literal("indefinite")]);
export type RetentionYears = z.infer<typeof RetentionYearsSchema>;
export const retentionToStored = (v: RetentionYears): number => (v === "indefinite" ? RETENTION_INDEFINITE_YEARS : v);
export const retentionFromStored = (n: number): RetentionYears => (n >= RETENTION_INDEFINITE_YEARS ? "indefinite" : n);

const IsoTimezone = z.string().min(3).max(64).refine((tz) => {
  try { new Intl.DateTimeFormat("en-ZA", { timeZone: tz }); return true; } catch { return false; }
}, "unknown IANA time zone");
const Locale = z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/, "BCP-47 language tag such as en-ZA");

/** Every organisation-level value the Owner may edit (UC-TENANCY-002). Ranges from BR-TENANCY-014. */
const OrganisationEditableShape = {
  legalName: z.string().trim().min(1).max(200),
  tradingName: z.string().trim().min(1).max(200).nullable(),
  vatRateBp: BpSchema.refine((n) => n >= 0 && n <= 10_000, "0 to 10 000 basis points"),
  locale: Locale,
  timezone: IsoTimezone,
  discountPinThresholdPercentBp: BpSchema.refine((n) => n >= 0 && n <= 10_000, "0 to 10 000 basis points"),
  discountPinThresholdCents: NonNegativeCentsSchema,
  pinMinLength: z.number().int().min(4).max(6),
  sessionIdleMinutesDesktop: z.number().int().min(1),
  sessionIdleMinutesTablet: z.number().int().min(1),
  sessionAbsoluteHours: z.number().int().min(1).max(24 * 7),
  editLockIdleMinutes: z.number().int().min(1).max(600),
  handoverIdleMinutes: z.number().int().min(1).max(600),
  patientSharingMode: z.enum(PATIENT_SHARING_MODES),
  retentionFinancialYears: RetentionYearsSchema,
  retentionClinicalYears: RetentionYearsSchema,
  retentionMinorUntilAge: z.number().int().min(0).max(120),
  retentionLogYears: RetentionYearsSchema,
  informationOfficerName: z.string().trim().max(200).nullable(),
  informationOfficerEmail: z.string().trim().toLowerCase().email().max(254).nullable(),
  informationOfficerPhone: z.string().trim().max(40).nullable(),
  /** Roles (or `BranchOwner`) allowed to authorise a step-up; null = any active user (R-07). */
  stepupAuthoriserRoles: z.array(z.enum(["Owner", "Admin", "Optometrist", "Dispenser", "FrameConsultant", "Receptionist", "BranchOwner"])).min(1).nullable(),
  isRoleBasedDiscountAuthEnabled: z.boolean(),
};

/** Idle timeouts may not outlast the absolute session (BR-TENANCY-014). Checked on the merged (stored + patch) values. */
export function organisationRangeErrors(v: { sessionIdleMinutesDesktop: number; sessionIdleMinutesTablet: number; sessionAbsoluteHours: number }): { path: string; code: string; message: string }[] {
  const absoluteMinutes = v.sessionAbsoluteHours * 60;
  const out: { path: string; code: string; message: string }[] = [];
  for (const key of ["sessionIdleMinutesDesktop", "sessionIdleMinutesTablet"] as const) {
    if (v[key] > absoluteMinutes) out.push({ path: key, code: "exceeds_absolute", message: `must not exceed the absolute session lifetime of ${absoluteMinutes} minutes` });
  }
  return out;
}

export const OrganisationPatchSchema = z.strictObject(Object.fromEntries(
  Object.entries(OrganisationEditableShape).map(([k, s]) => [k, s.optional()]),
) as { [K in keyof typeof OrganisationEditableShape]: z.ZodOptional<(typeof OrganisationEditableShape)[K]> });
export type OrganisationPatch = z.infer<typeof OrganisationPatchSchema>;

/** Validates the merged organisation settings as one object — the unit test surface for BR-TENANCY-014 (TC-TENANCY-005). */
export const OrganisationSettingsSchema = z.strictObject(OrganisationEditableShape).superRefine((v, ctx) => {
  for (const e of organisationRangeErrors(v)) ctx.addIssue({ code: "custom", path: [e.path], message: e.message, params: { code: e.code } });
});

export const OrganisationViewSchema = z.object({
  id: UlidSchema,
  ...OrganisationEditableShape,
  commercialModel: z.enum(COMMERCIAL_MODELS),
  franchisorOrgId: UlidSchema.nullable(),
  status: z.enum(ORGANISATION_STATUSES),
  country: z.string(),
  currency: z.string(),
  pinMaxLength: z.number().int(),
  accountingProvider: z.enum(ACCOUNTING_PROVIDERS),
  crmProvider: z.enum(CRM_PROVIDERS),
  version: z.number().int(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type OrganisationView = z.infer<typeof OrganisationViewSchema>;

// --- Feature flags (UC-TENANCY-008) --------------------------------------------------------------------------------------

export const FeatureFlagsViewSchema = z.object(Object.fromEntries(FEATURE_FLAG_KEYS.map((k) => [k, z.boolean()])) as Record<FeatureFlagKey, z.ZodBoolean>);
export const FeatureFlagsPatchSchema = z.strictObject(Object.fromEntries(FEATURE_FLAG_KEYS.map((k) => [k, z.boolean().optional()])) as Record<FeatureFlagKey, z.ZodOptional<z.ZodBoolean>>);
export type FeatureFlagsView = z.infer<typeof FeatureFlagsViewSchema>;

// --- PIN-protected actions (UC-TENANCY-009) ------------------------------------------------------------------------------

export const PinProtectedActionsViewSchema = z.object({
  /** Stored actions currently requiring step-up. */
  required: z.array(z.enum(CONTROL_LOG_ACTIONS)),
  /** Every stored action the Owner may toggle. */
  available: z.array(z.enum(CONTROL_LOG_ACTIONS)),
  /**
   * The D20 administrative actions and, since DVN's answer of 24 Oct 2026, `StockTakeFinalise`. They are rows of the set (listed
   * in `available` and always in `required`), but they cannot be switched off: a PUT that omits one is refused 422 and the
   * step-up prompt for them ignores the stored row (BR-TENANCY-011). Named here so the client can show them locked.
   */
  alwaysRequired: z.array(z.enum(ALWAYS_REQUIRED_ACTIONS)),
});
export const PinProtectedActionsPutSchema = z.strictObject({ required: z.array(z.enum(CONTROL_LOG_ACTIONS)).max(CONTROL_LOG_ACTIONS.length), reason: z.string().trim().max(500).optional() });

// --- Branches (UC-TENANCY-003..006, 010) -----------------------------------------------------------------------------------

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

export const BranchInputSchema = z.strictObject({
  name: z.string().trim().min(1).max(120),
  code: BranchCodeSchema,
  addressLine1: optionalText(200),
  addressLine2: optionalText(200),
  suburb: optionalText(120),
  city: optionalText(120),
  province: optionalText(120),
  postalCode: optionalText(20),
  country: z.string().trim().length(2).toUpperCase().optional(),
  vatNo: optionalText(20),
  isVatRegistered: z.boolean().optional(),
  companyRegNo: optionalText(40),
  email: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
  phone: optionalText(40),
  /** Seven digits in South Africa; a different length is a warning, not an error (TEN-22). */
  practiceNumber: optionalText(20),
  timezone: IsoTimezone.optional(),
  bankName: optionalText(120),
  bankAccountNo: optionalText(40),
  bankBranchCode: optionalText(20),
  dailySummaryExtraRecipients: z.array(z.string().trim().toLowerCase().email()).max(20).optional(),
  isDailySummaryEnabled: z.boolean().optional(),
}).superRefine((v, ctx) => {
  if (v.isVatRegistered !== false && !v.vatNo) ctx.addIssue({ code: "custom", path: ["vatNo"], message: "VAT number is required for a VAT-registered branch", params: { code: "required" } });
});
export type BranchInput = z.infer<typeof BranchInputSchema>;

export const BranchPatchSchema = z.strictObject({
  name: z.string().trim().min(1).max(120).optional(),
  code: BranchCodeSchema.optional(),
  addressLine1: optionalText(200), addressLine2: optionalText(200), suburb: optionalText(120), city: optionalText(120),
  province: optionalText(120), postalCode: optionalText(20), country: z.string().trim().length(2).toUpperCase().optional(),
  vatNo: optionalText(20), isVatRegistered: z.boolean().optional(), companyRegNo: optionalText(40),
  email: z.string().trim().toLowerCase().email().max(254).nullable().optional(), phone: optionalText(40), practiceNumber: optionalText(20),
  timezone: IsoTimezone.optional(), bankName: optionalText(120), bankAccountNo: optionalText(40), bankBranchCode: optionalText(20),
  dailySummaryExtraRecipients: z.array(z.string().trim().toLowerCase().email()).max(20).optional(), isDailySummaryEnabled: z.boolean().optional(),
});
export type BranchPatch = z.infer<typeof BranchPatchSchema>;

export const WarningSchema = z.object({ path: z.string(), code: z.string(), message: z.string() });
export type Warning = z.infer<typeof WarningSchema>;

export const BranchViewSchema = z.object({
  id: UlidSchema,
  name: z.string(),
  code: z.string(),
  addressLine1: z.string().nullable(), addressLine2: z.string().nullable(), suburb: z.string().nullable(), city: z.string().nullable(),
  province: z.string().nullable(), postalCode: z.string().nullable(), country: z.string(),
  vatNo: z.string().nullable(), isVatRegistered: z.boolean(), companyRegNo: z.string().nullable(),
  email: z.string().nullable(), phone: z.string().nullable(), practiceNumber: z.string().nullable(),
  status: z.enum(BRANCH_STATUSES), timezone: z.string(),
  bankName: z.string().nullable(), bankAccountNo: z.string().nullable(), bankBranchCode: z.string().nullable(),
  dailySummaryExtraRecipients: z.array(z.string()), isDailySummaryEnabled: z.boolean(),
  /** Whether any document number has been issued with this code — the code is immutable once true (R-11). */
  numberingStarted: z.boolean(),
  branchOwnerUserIds: z.array(UlidSchema),
  deactivatedAt: InstantSchema.nullable(), deactivatedBy: UlidSchema.nullable(), deactivationReason: z.string().nullable(),
  version: z.number().int(), createdAt: InstantSchema, updatedAt: InstantSchema,
});
export type BranchView = z.infer<typeof BranchViewSchema>;
export const BranchCreatedSchema = BranchViewSchema.extend({ warnings: z.array(WarningSchema) });

export const BranchLifecycleRequestSchema = z.strictObject({ reason: z.string().trim().min(1).max(500).optional() });
export const BranchLifecycleResponseSchema = BranchViewSchema.extend({
  /** Users whose only live assignment is this branch — flagged for reassignment (TEN-20). */
  soleAssignmentUsers: z.array(z.object({ userId: UlidSchema, displayName: z.string(), controlIdDisplay: z.string() })),
  changed: z.boolean(),
});

export const SupplierAccountInputSchema = z.strictObject({
  supplierId: z.string().min(1).max(64),
  accountNumber: z.string().trim().min(1).max(64),
  isDefaultForLenses: z.boolean().optional(),
  isDefaultForFitting: z.boolean().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});
export const SupplierAccountsPutSchema = z.strictObject({ accounts: z.array(SupplierAccountInputSchema).max(50) }).superRefine((v, ctx) => {
  if (v.accounts.filter((a) => a.isDefaultForLenses).length > 1) ctx.addIssue({ code: "custom", path: ["accounts"], message: "only one supplier may be the default for lenses", params: { code: "duplicate_default_lenses" } });
  if (v.accounts.filter((a) => a.isDefaultForFitting).length > 1) ctx.addIssue({ code: "custom", path: ["accounts"], message: "only one supplier may be the default for fitting", params: { code: "duplicate_default_fitting" } });
  const ids = v.accounts.map((a) => a.supplierId);
  if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", path: ["accounts"], message: "a supplier appears more than once", params: { code: "duplicate_supplier" } });
});
/**
 * BR-TENANCY-034 (W14): each account names its supplier and says whether it is active, read when the list is read, so a
 * renamed supplier shows its new name; the list is by supplier name.
 */
export const SupplierAccountViewSchema = z.object({
  id: UlidSchema, supplierId: z.string(), supplierName: z.string(), supplierStatus: z.enum(SUPPLIER_STATUSES),
  accountNumber: z.string(), isDefaultForLenses: z.boolean(), isDefaultForFitting: z.boolean(), notes: z.string().nullable(),
});
export const SupplierAccountsViewSchema = z.object({ branchId: UlidSchema, accounts: z.array(SupplierAccountViewSchema) });

// --- The setting store (UC-TENANCY-007; TEN-21) --------------------------------------------------------------------------

export const SETTING_NAMESPACES = ["sales", "pricing", "catalogue", "stock", "labs", "cashup", "documents", "messaging", "security", "reporting", "patients"] as const;
export type SettingNamespace = (typeof SETTING_NAMESPACES)[number];
export type SettingValueType = "string" | "number" | "boolean" | "json";
export type SettingSource = "branch" | "tenant" | "default";

export interface SettingDefinition {
  readonly namespace: SettingNamespace;
  readonly key: string;
  readonly valueType: SettingValueType;
  readonly schema: z.ZodType;
  /** System default, used when neither a branch nor a tenant value is stored (`source: 'default'`). */
  readonly defaultValue: unknown;
  /** Organisation column that holds the tenant-level value, when the key mirrors one (TEN-13 settings live on the org row). */
  readonly organisationField?: keyof OrganisationView;
}

/**
 * PAT-54 / BR-PATIENTS-040: the automatic-dormancy threshold in whole months — at least a year, so a patient on an ordinary
 * one-year recall is never made Dormant; 24 by default (Q-18's suggestion).
 */
export const AUTO_DORMANCY_MONTHS = { min: 12, max: 120, default: 24 } as const;

/**
 * The rounding rules of BR-PRICING-003 (CAT-36; W3 adds `NearestR10`), applied to a price a markup computed: `None` as
 * computed; `NearestR1` to the nearest R 1.00, halves up; `UpToNearestR5` and `UpToNearestR10` up to the next multiple;
 * `NearestR10` to the nearest R 10.00, halves up; `ToX99` and `ToX95` up to the next price ending in 99 or 95 cents. The
 * money package's `roundPrice` applies them (TC-MONEY-008..014); the `pricing` settings `roundingRule` and
 * `lensRoundingRule` choose one each (BR-PRICING-018).
 */
export const ROUNDING_RULES = ["None", "NearestR1", "UpToNearestR5", "UpToNearestR10", "NearestR10", "ToX99", "ToX95"] as const;
export type RoundingRule = (typeof ROUNDING_RULES)[number];

/**
 * The registered keys. A key not listed here is 422 on write; a namespace not listed is 404 (UC-TENANCY-007 E2). Every key
 * resolves branch override → tenant value → default (BR-TENANCY-006), so every key may be overridden at a branch.
 */
export const SETTING_DEFINITIONS: readonly SettingDefinition[] = [
  { namespace: "sales", key: "discountPinThresholdPercentBp", valueType: "number", schema: z.number().int().min(0).max(10_000), defaultValue: 0, organisationField: "discountPinThresholdPercentBp" },
  { namespace: "sales", key: "discountPinThresholdCents", valueType: "number", schema: z.number().int().min(0), defaultValue: 0, organisationField: "discountPinThresholdCents" },
  { namespace: "sales", key: "quoteValidityDays", valueType: "number", schema: z.number().int().min(1).max(365), defaultValue: 30 },
  { namespace: "cashup", key: "floatCents", valueType: "number", schema: z.number().int().min(0), defaultValue: 100_000 },
  // PAT-54 / BR-PATIENTS-040: automatic dormancy, off unless the practice switches it on; 24 months by default (Q-18).
  { namespace: "patients", key: "autoDormancyEnabled", valueType: "boolean", schema: z.boolean(), defaultValue: false },
  { namespace: "patients", key: "autoDormancyMonths", valueType: "number", schema: z.number().int().min(AUTO_DORMANCY_MONTHS.min).max(AUTO_DORMANCY_MONTHS.max), defaultValue: AUTO_DORMANCY_MONTHS.default },
  { namespace: "security", key: "sessionIdleMinutesDesktop", valueType: "number", schema: z.number().int().min(1), defaultValue: 30, organisationField: "sessionIdleMinutesDesktop" },
  { namespace: "security", key: "sessionIdleMinutesTablet", valueType: "number", schema: z.number().int().min(1), defaultValue: 10, organisationField: "sessionIdleMinutesTablet" },
  { namespace: "security", key: "pinMinLength", valueType: "number", schema: z.number().int().min(4).max(6), defaultValue: 4, organisationField: "pinMinLength" },
  { namespace: "documents", key: "invoiceFooterText", valueType: "string", schema: z.string().max(2000), defaultValue: "" },
  { namespace: "documents", key: "showBankDetailsOnInvoice", valueType: "boolean", schema: z.boolean(), defaultValue: true },
  { namespace: "messaging", key: "quietHours", valueType: "json", schema: z.object({ from: z.string().regex(/^\d{2}:\d{2}$/), to: z.string().regex(/^\d{2}:\d{2}$/) }), defaultValue: { from: "20:00", to: "07:00" } },
  // BR-APPOINTMENTS-018: off by default while Zoho Bookings sends its own reminders (UC-APPOINTMENTS-014 open question); Ruahan decides.
  { namespace: "messaging", key: "appointmentRemindersEnabled", valueType: "boolean", schema: z.boolean(), defaultValue: false },
  // Sprint 4 (W7, UC-TENANCY-007 open questions; for Khaalid to confirm). The organisation's values of the `catalogue`,
  // `pricing` and `stock` namespaces are written through `/catalogue-settings` (`catalogue.settings.write`, UC-CATALOGUE-011,
  // UC-PRICING-006), a branch's override through the store's own routes (D40); the `stock` namespace holds no key yet.
  // BR-CATALOGUE-029: whether a new frame starts serialised, and whether a lens the price-list import creates starts made to
  // order; each on by default and overridable at a branch (Proposed, UC-CATALOGUE-011 open questions).
  { namespace: "catalogue", key: "framesSerialisedByDefault", valueType: "boolean", schema: z.boolean(), defaultValue: true },
  { namespace: "catalogue", key: "importCreatesNonStockedLenses", valueType: "boolean", schema: z.boolean(), defaultValue: true },
  // BR-PRICING-018: the rounding of frame bands and product markups, the rounding of prices derived from a lens price list
  // (DVN's `NearestR10`, FIXTURE §5), and whether a receipt proposes a price (CAT-81). The price-override floor is not a
  // pricing key: it is the organisation's discount PIN threshold (BR-PRICING-017).
  { namespace: "pricing", key: "roundingRule", valueType: "string", schema: z.enum(ROUNDING_RULES), defaultValue: "NearestR1" },
  { namespace: "pricing", key: "lensRoundingRule", valueType: "string", schema: z.enum(ROUNDING_RULES), defaultValue: "NearestR1" },
  { namespace: "pricing", key: "proposePriceAtReceipt", valueType: "boolean", schema: z.boolean(), defaultValue: true },
];

export function settingDefinitions(namespace: SettingNamespace): readonly SettingDefinition[] {
  return SETTING_DEFINITIONS.filter((d) => d.namespace === namespace);
}
export function settingDefinition(namespace: string, key: string): SettingDefinition | undefined {
  return SETTING_DEFINITIONS.find((d) => d.namespace === namespace && d.key === key);
}
export const isSettingNamespace = (s: string): s is SettingNamespace => (SETTING_NAMESPACES as readonly string[]).includes(s);

export const ResolvedSettingSchema = z.object({ value: z.unknown(), source: z.enum(["branch", "tenant", "default"]) });
export const SettingsScopeViewSchema = z.object({
  scopeKey: z.string(),
  namespace: z.enum(SETTING_NAMESPACES),
  settings: z.record(z.string(), ResolvedSettingSchema),
});
export const EffectiveSettingsSchema = z.object({
  branchId: UlidSchema.nullable(),
  settings: z.record(z.string(), z.record(z.string(), ResolvedSettingSchema)),
  /** The active patient titles in sort order (PAT-02, UC-PATIENTS-006) — every role reads the picker from here. */
  patientTitles: z.array(z.object({ code: z.string(), label: z.string() })),
});
/** PUT body: a partial map of registered keys to values; each value is validated against its definition server-side. */
export const SettingsPutSchema = z.record(z.string().min(1).max(64), z.unknown()).refine((v) => Object.keys(v).length > 0, "at least one key");
